<template>
  <div>
    <teleport to="body">
      <!-- Floating Assistant Orb -->
      <div class="va-assistant-orb" :class="{ 'android-device': isAndroid }" @click="handleAvatarClick">
        <v-tooltip location="top">
          <template #activator="{ props }">
            <div v-bind="props" class="va-orb-container" :class="{ active: isDialogOpen }">
              <div class="va-orb-glow"></div>
              <div class="va-orb-inner">
                <div ref="lottieAgentRef" class="va-orb-avatar"></div>
              </div>
              <div class="va-orb-pulse"></div>
              <div class="va-orb-pulse va-orb-pulse-2"></div>
            </div>
          </template>
          <span>{{ isDialogOpen ? $t('chat.agent.avatar.tooltip-listening') : $t('chat.agent.avatar.tooltip-ask') }}</span>
        </v-tooltip>
      </div>

      <!-- Modern Dialog Interface -->
      <transition name="va-dialog-transition">
        <div v-if="isDialogOpen" class="va-assistant-interface" :class="{ 'is-premium-user': isPremiumUser }">
          <!-- Backdrop with blur -->
          <div class="va-interface-backdrop" @click="handleCancel"></div>

          <!-- Main Assistant Card -->
          <div class="va-assistant-card">
            <div style="position: absolute; top: 12px; right: 16px; z-index: 2;">
              <button class="va-close-btn" @click="handleCancel" style="margin-top: 0;">
                <i class="mdi mdi-close"></i>
              </button>
            </div>

            <!-- Scrollable Content -->
            <div class="va-card-body">
              <!-- Central Animation -->
              <div class="va-animation-section">
                <div class="va-animation-wrapper">
                  <div class="va-animation-backdrop"></div>
                  <div ref="lottieContainer" class="va-lottie-container" :class="{ speaking: hasStartedSpeaking }"></div>
                  <div class="va-sound-waves" v-if="hasStartedSpeaking && !isPaused">
                    <span class="va-wave"></span>
                    <span class="va-wave"></span>
                    <span class="va-wave"></span>
                  </div>
                </div>
              </div>

              <!-- Premium Feature Info -->
              <div class="va-premium-info">
                <v-icon color="#F59E0B" size="20" class="va-premium-info-icon">mdi-star</v-icon>
                <div class="va-premium-info-text">
                  <strong>{{ $t('chat.agent.premium.feature') }}</strong>{{ $t('chat.agent.premium.description') }}
                </div>
              </div>

              <!-- Status Message -->
              <transition name="va-fade">
                <div v-if="showDelayMessage" class="va-status-message">
                  <span class="va-status-dot"></span>
                  {{ $t('chat.agent.delay.message') }}
                </div>
              </transition>

              <!-- Primary Controls -->
              <div class="va-controls-section">
                <button v-if="!hasStartedSpeaking" class="va-control-btn va-btn-primary" :disabled="isTextSending || isVoiceSending" @click="startSpeaking">
                  <i class="mdi mdi-microphone" style="font-size:30px"></i>
                  <span style="font-size:larger">{{ $t('chat.agent.buttons.speak') }}</span>
                </button>

                <button v-else class="va-control-btn va-btn-secondary" :disabled="isPausing || isTextSending || isVoiceSending" @click="togglePause">
                  <span v-if="isPausing" class="va-spinner"></span>
                  <i v-else style="font-size:30px" class="mdi" :class="isPaused ? 'mdi-play' : 'mdi-pause'"></i>
                  <span style="font-size:larger">{{ isPaused ? $t('chat.agent.buttons.resume') : $t('chat.agent.buttons.pause') }}</span>
                </button>

                <button class="va-control-btn va-btn-tertiary" :disabled="isTextSending || isVoiceSending" @click="openChatWindow">
                  <i class="mdi mdi-message-text" style="font-size:30px"></i>
                  <span style="font-size:larger">{{ $t('chat.agent.buttons.chat') }}</span>
                </button>
              </div>

              <!-- Send Actions -->
              <div class="va-send-actions">
                <button class="va-send-btn va-text-reply" :class="{ loading: isTextSending }" :disabled="isTextSending || isVoiceSending" @click="handleTextReply">
                  <div class="va-send-btn-icon">
                    <span v-if="isTextSending" class="va-spinner"></span>
                    <i v-else class="mdi mdi-keyboard"></i>
                  </div>
                  <div class="va-send-btn-content">
                    <strong>{{ $t('chat.agent.buttons.send') }}</strong>
                    <span>{{ $t('chat.agent.buttons.text-reply') }}</span>
                  </div>
                  <i class="mdi mdi-chevron-right va-send-btn-arrow"></i>
                </button>

                <button class="va-send-btn va-voice-reply" :class="{ loading: isVoiceSending }" :disabled="isTextSending || isVoiceSending" @click="handleVoiceReply">
                  <div class="va-send-btn-icon">
                    <span v-if="isVoiceSending" class="va-spinner"></span>
                    <i v-else class="mdi mdi-microphone"></i>
                  </div>
                  <div class="va-send-btn-content">
                    <strong>{{ $t('chat.agent.buttons.send') }}</strong>
                    <span>{{ $t('chat.agent.buttons.voice-reply') }}</span>
                  </div>
                  <i class="mdi mdi-chevron-right va-send-btn-arrow"></i>
                </button>
              </div>

              <!-- Controls Guide -->
              <div class="va-guide-section">
                <v-card class="va-guide-card" flat>
                  <div class="va-guide-item">
                    <v-chip class="va-guide-chip" color="white" label size="small" style="background:linear-gradient(135deg, #10b981 0%, #059669 100%);font-weight:bold">
                      <v-icon start size="small">mdi-microphone</v-icon>
                      {{ $t('chat.agent.controls-guide.speak.label') }}
                    </v-chip>
                    <span class="va-guide-text">{{ $t('chat.agent.controls-guide.speak.description') }}</span>
                  </div>
                  <div class="va-guide-item">
                    <v-chip
                      class="va-guide-chip"
                      color="white"
                      label
                      size="small"
                      style="background:linear-gradient(135deg, #818cf8 0%, #6366f1 50%, #ec4899 100%);font-weight:bold"
                    >
                      <v-icon start size="small">mdi-pause-circle</v-icon>
                      {{ $t('chat.agent.controls-guide.pause.label') }}
                    </v-chip>
                    <span class="va-guide-text">{{ $t('chat.agent.controls-guide.pause.description') }}</span>
                  </div>
                  <div class="va-guide-item">
                    <v-chip class="va-guide-chip" label size="small" color="white" style="background:linear-gradient(135deg, #38bdf8 0%, #2563eb 100%);font-weight:bold">
                      <v-icon start size="small">mdi-chat</v-icon>
                      {{ $t('chat.agent.controls-guide.chat.label') }}
                    </v-chip>
                    <span class="va-guide-text">{{ $t('chat.agent.controls-guide.chat.description') }}</span>
                  </div>
                  <div class="va-guide-item">
                    <v-chip class="va-guide-chip" color="white" label size="small" style="background:linear-gradient(135deg, #dc2626 0%, #991b1b 100%);font-weight:bold">
                      <v-icon start size="small">mdi-send</v-icon>
                      {{ $t('chat.agent.controls-guide.send-text.label') }}
                    </v-chip>
                    <span class="va-guide-text">{{ $t('chat.agent.controls-guide.send-text.description') }}</span>
                  </div>
                  <div class="va-guide-item">
                    <v-chip class="va-guide-chip" color="white" label size="small" style="background:linear-gradient(90deg, #E63946, #F28C38);font-weight:bold">
                      <v-icon start size="small">mdi-volume-high</v-icon>
                      {{ $t('chat.agent.controls-guide.send-voice.label') }}
                    </v-chip>
                    <span class="va-guide-text">{{ $t('chat.agent.controls-guide.send-voice.description') }}</span>
                  </div>
                </v-card>
              </div>
            </div>
          </div>
        </div>
      </transition>

      <!-- Chat Dialog -->
      <v-dialog v-model="chatVisible" max-width="600" scrollable>
        <ChatBotComponent v-if="chatVisible" :initialMessage="transcribedText" @close="chatVisible = false" />
      </v-dialog>
    </teleport>
  </div>
</template>

<script setup>
  import { ref, nextTick, onMounted, onUnmounted } from 'vue'
  import { VoiceRecorder } from 'capacitor-voice-recorder'
  import lottie from 'lottie-web'
  import Swal from '@/services/ui/dialog'
  import ChatBotComponent from '@/ai/aiComponents/ChatBotComponent.vue'
  import lottieAgent from '@/ai/aiAnimations/lottie_customer_service.json'
  import lottieAgentActive from '@/ai/aiAnimations/lottie_customer_active.json'
  import bubbleAnimation from '@/ai/aiAnimations/voice.json'
  import activateSoundUrl from '@/ai/aiSounds/activate.mp3'
  import deactivateSoundUrl from '@/ai/aiSounds/deactivate.mp3'
  import { sendToAssistant } from '@/ai/aiServices/assistantService'
  import { loadMessages, saveMessages } from '@/ai/aiServices/chatStorage'
  import { speakWithElevenLabs } from '@/ai/aiServices/tts'
  import { elevenLabsSTT, deepgramSTT } from '@/ai/aiServices/speechToText'
  import { speakWithDeepgram } from '../aiServices/tts_deepgram'
  import { showToast } from '@/services/ui/toast'
  import { Capacitor } from '@capacitor/core'
  import { getUserEmail } from '@/controllers/BaseController'

  const isDialogOpen = ref(false)
  const chatVisible = ref(false)
  const isAndroid = ref(Capacitor.getPlatform() === 'android')
  const isPremiumUser = ref(false)

  const transcribedText = ref('')
  const isPaused = ref(false)
  const isTextSending = ref(false)
  const isVoiceSending = ref(false)
  const isCancelling = ref(false)
  const isPausing = ref(false)
  const hasStartedSpeaking = ref(false)
  const showDelayMessage = ref(false)
  const detectedLanguage = ref('en')

  const lottieContainer = ref(null)
  const lottieAgentRef = ref(null)
  let lottieInstance = null
  let lottieAgentInstance = null

  const activateSound = new Audio(activateSoundUrl)
  const deactivateSound = new Audio(deactivateSoundUrl)
  const isIosNative = Capacitor.getPlatform() === 'ios' && Capacitor.isNativePlatform()

  onMounted(() => {
      if (lottieAgentRef.value) {
          lottieAgentInstance = lottie.loadAnimation({
              container: lottieAgentRef.value,
              renderer: 'svg',
              loop: true,
              autoplay: true,
              animationData: lottieAgent,
          })
      }

      // Check if user has premium access
      checkPremiumAccess()
  })

  async function checkPremiumAccess() {
      try {
          const userEmail = await getUserEmail()
          const emailLower = userEmail?.toLowerCase() || ''
          isPremiumUser.value = emailLower.includes('@pinestack.io')
          
          console.log('=== PREMIUM ACCESS CHECK ===')
          console.log('Email:', userEmail)
          console.log('Email (lowercase):', emailLower)
          console.log('Contains @pinestack.io:', emailLower.includes('@pinestack.io'))
          console.log('isPremiumUser:', isPremiumUser.value)
          console.log('===========================')
      } catch (error) {
          console.error('Failed to check premium access:', error)
          isPremiumUser.value = false
      }
  }

  onUnmounted(() => {
      lottieInstance?.destroy()
      lottieAgentInstance?.destroy()
  })

  function resetAllState() {
      isDialogOpen.value = false
      isTextSending.value = false
      isVoiceSending.value = false
      isPaused.value = false
      isPausing.value = false
      isCancelling.value = false
      hasStartedSpeaking.value = false
      transcribedText.value = ''
  }

  async function handleAvatarClick() {
      await VoiceRecorder.stopRecording().catch(() => { })
      await openDialog()
  }

  function showPermissionDenied() {
      Swal.fire({ icon: 'warning', title: 'Permission Denied', text: 'Microphone access is required.' })
  }

  function showError(title, message) {
      Swal.fire({ icon: 'error', title, text: message })
  }

  function swapLottieAnimation(animationData) {
      if (lottieContainer.value) {
          lottieInstance?.destroy()
          lottieInstance = lottie.loadAnimation({
              container: lottieContainer.value,
              renderer: 'svg',
              loop: true,
              autoplay: true,
              animationData,
          })
      }
  }

  async function ensureSpeechPermission() {
      const canRecord = await VoiceRecorder.canDeviceVoiceRecord()
      if (!canRecord.value) return false
      const hasPermission = await VoiceRecorder.hasAudioRecordingPermission()
      if (!hasPermission.value) {
          const result = await VoiceRecorder.requestAudioRecordingPermission()
          return result.value === true
      }
      return true
  }

  async function openDialog() {
      isDialogOpen.value = true
      hasStartedSpeaking.value = false
      await nextTick()
      preloadLottie()
      lottieInstance?.pause()
      const status = await VoiceRecorder.getCurrentStatus()
      if (status.status !== 'NONE') await VoiceRecorder.stopRecording()
  }

  function preloadLottie() {
      if (lottieContainer.value) {
          lottieInstance?.destroy()
          lottieInstance = lottie.loadAnimation({
              container: lottieContainer.value,
              renderer: 'svg',
              loop: true,
              autoplay: true,
              animationData: bubbleAnimation,
          })
      }
  }

  async function startSpeaking() {
      const granted = await ensureSpeechPermission()
      if (!granted) return showPermissionDenied()

      const status = await VoiceRecorder.getCurrentStatus()
      if (status.status !== 'NONE') await VoiceRecorder.stopRecording()

      lottieInstance?.play()
      await VoiceRecorder.startRecording()
      if (!isIosNative) {
          activateSound.play().catch(() => { })
      }

      hasStartedSpeaking.value = true
      isPaused.value = false
  }

  async function togglePause() {
      isPausing.value = true
      try {
          if (isPaused.value) {
              await VoiceRecorder.resumeRecording()
              lottieInstance?.play()
          } else {
              await VoiceRecorder.pauseRecording()
              lottieInstance?.pause()
          }
          isPaused.value = !isPaused.value
      } catch (err) {
          showError('Mic Error', err.message)
      } finally {
          isPausing.value = false
      }
  }

  async function handleCancel() {
      try {
          const status = await VoiceRecorder.getCurrentStatus()
          if (status.status !== 'NONE') await VoiceRecorder.stopRecording()
          deactivateSound.play().catch(() => { })
      } catch (err) {
          showError('Cancel Error', err.message)
      } finally {
          resetAllState()
      }
  }

  function openChatWindow() {
      VoiceRecorder.stopRecording().catch(() => { })
      deactivateSound.play().catch(() => { })
      isDialogOpen.value = false
      if (chatVisible.value) {
          window.dispatchEvent(new CustomEvent('voice-transcribed', { detail: transcribedText.value }))
      } else {
          chatVisible.value = true
      }
      transcribedText.value = ''
  }

  async function handleVoiceReply() {
      if (!hasStartedSpeaking.value) {
          return showToast('Please start speaking or open chat', 'warning')
      }

      isVoiceSending.value = true

      try {
          const result = await VoiceRecorder.stopRecording()
          const base64 = result?.value?.recordDataBase64
          if (!base64) throw new Error('No audio data')

          const blob = base64ToBlob(base64, result.value.mimeType || 'audio/webm')

          const { text: cleanText, language_code } = await elevenLabsSTT(blob)
          if (!cleanText) return

          swapLottieAnimation(lottieAgent)

          transcribedText.value = cleanText
          console.log('📝 Transcribed:', cleanText)
          console.log('🌐 STT Language Code:', language_code)

          const lang = language_code?.toLowerCase().startsWith('de') ? 'de' : 'en'
          detectedLanguage.value = lang
          const prefix = lang === 'de'
              ? 'Bitte antworte auf Deutsch und optimiere deine Antwort für die Sprachausgabe (locker gesprochen).\n'
              : 'Please reply in English and optimize your response for voice delivery (natural spoken).\n'

          showDelayMessage.value = false
          const delayTimer = setTimeout(() => {
              showDelayMessage.value = true
          }, 3000)

          const assistantResponse = await sendToAssistant(prefix + cleanText)
          const reply = assistantResponse?.reply?.trim()
          if (!reply) return

          let audio
          if (lang === 'en') {
              audio = await speakWithDeepgram(reply)
          } else {
              audio = await speakWithElevenLabs(reply, 'de')
          }

          clearTimeout(delayTimer)
          showDelayMessage.value = false

          if (audio) {
              audio.onended = () => {
                  swapLottieAnimation(bubbleAnimation)
                  lottieInstance?.pause()
              }
          } else {
              swapLottieAnimation(bubbleAnimation)
              lottieInstance?.pause()
          }

          const old = (await loadMessages()) || []
          old.push({ type: 'user', text: cleanText }, { type: 'bot', text: reply })
          await saveMessages(old)

      } catch (err) {
          showError('Voice Reply Error', err.message)
          swapLottieAnimation(bubbleAnimation)
          lottieInstance?.stop()
      } finally {
          deactivateSound.play().catch(() => { })
          isVoiceSending.value = false
          hasStartedSpeaking.value = false
          isPaused.value = false
      }
  }

  async function handleTextReply() {
      if (!hasStartedSpeaking.value) return showToast('Please start speaking or open chat', 'warning')
      isTextSending.value = true
      try {
          const result = await VoiceRecorder.stopRecording()
          const base64 = result.value.recordDataBase64
          if (!base64) throw new Error('No audio data')
          const blob = base64ToBlob(base64, result.value.mimeType || 'audio/webm')

          transcribedText.value = await elevenLabsSTT(blob)
          if (chatVisible.value) {
              window.dispatchEvent(new CustomEvent('voice-transcribed', { detail: transcribedText.value }))
          } else {
              chatVisible.value = true
          }
          transcribedText.value = ''
      } catch (err) {
          showError('Transcription Error', err.message)
      } finally {
          deactivateSound.play().catch(() => { })
          isTextSending.value = false
      }
  }

  function base64ToBlob(base64, mimeType) {
      const byteCharacters = atob(base64)
      const byteArrays = []
      for (let offset = 0; offset < byteCharacters.length; offset += 512) {
          const slice = byteCharacters.slice(offset, offset + 512)
          const byteNumbers = Array.from(slice).map((c) => c.charCodeAt(0))
          byteArrays.push(new Uint8Array(byteNumbers))
      }
      return new Blob(byteArrays, { type: mimeType })
  }
</script>
<style scoped>
  @import '@/ai/aiStyles/AgentStyle.css';

  /* ==================== FLOATING ORB - COMPLETE CSS ==================== */
  
  /* Custom Properties for Orb */
  .va-assistant-orb {
    --va-primary: #6366f1;
    --va-secondary: #ec4899;
    --va-shadow-lg: 0 10px 15px -3px rgb(0 0 0 / 0.1);
    --va-radius-full: 9999px;
    --va-ease-out: cubic-bezier(0, 0, 0.2, 1);
  }

  /* ==================== FLOATING ORB BASE ==================== */
  .va-assistant-orb {
    position: fixed !important;
    bottom: calc(60px + 1.5rem) !important;
    right: 2rem !important;
    left: auto !important;
    z-index: 1000 !important;
    transform: translateZ(0) !important;
    -webkit-transform: translateZ(0) !important;
  }

  /* Android has system nav bar below the app nav bar */
  .va-assistant-orb.android-device {
    bottom: calc(120px + 1.5rem) !important;
  }

  .va-orb-container {
    position: relative !important;
    width: 4rem !important;
    height: 4rem !important;
    cursor: pointer !important;
    transition: transform 0.3s var(--va-ease-out) !important;
  }

  .va-orb-container:hover {
    transform: scale(1.1) !important;
  }

  .va-orb-container.active {
    animation: va-orbBounce 2s ease-in-out infinite !important;
  }

  .va-orb-inner {
    position: relative !important;
    width: 100% !important;
    height: 100% !important;
    border-radius: var(--va-radius-full) !important;
    background: linear-gradient(135deg, var(--va-primary) 0%, var(--va-secondary) 100%) !important;
    box-shadow: var(--va-shadow-lg) !important;
    overflow: hidden !important;
  }

  .va-orb-avatar {
    width: 100% !important;
    height: 100% !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }

  .va-orb-glow {
    position: absolute !important;
    inset: -0.5rem !important;
    background: radial-gradient(circle, rgba(99, 102, 241, 0.3) 0%, transparent 70%) !important;
    border-radius: var(--va-radius-full) !important;
    filter: blur(8px) !important;
    animation: va-glow 3s ease-in-out infinite !important;
  }

  .va-orb-pulse {
    position: absolute !important;
    inset: 0 !important;
    border: 2px solid rgba(99, 102, 241, 0.4) !important;
    border-radius: var(--va-radius-full) !important;
    animation: va-pulse 2s ease-out infinite !important;
  }

  .va-orb-pulse-2 {
    animation-delay: 1s !important;
  }

  /* ==================== ORB ANIMATIONS ==================== */
  @keyframes va-orbBounce {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-8px);
    }
  }

  @keyframes va-glow {
    0%, 100% {
      opacity: 0.3;
    }
    50% {
      opacity: 0.6;
    }
  }

  @keyframes va-pulse {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    100% {
      transform: scale(1.5);
      opacity: 0;
    }
  }

  /* ==================== PREMIUM VOICE FEATURES - CONDITIONAL DISABLED STATE ==================== */
  
  /* Speak button - Always show premium badge, only disable for non-premium users */
  .va-btn-primary {
    position: relative !important;
    overflow: visible !important;
  }

  .va-btn-primary::before {
    content: '\F04CE Premium' !important;
    position: absolute !important;
    top: -10px !important;
    right: 8px !important;
    background: linear-gradient(135deg, #DC3545 0%, #C82333 100%) !important;
    color: white !important;
    font-size: 0.625rem !important;
    font-weight: 700 !important;
    padding: 4px 10px !important;
    border-radius: 12px !important;
    box-shadow: 0 2px 8px rgba(220, 53, 69, 0.5) !important;
    letter-spacing: 0.5px !important;
    z-index: 10 !important;
    white-space: nowrap !important;
    font-family: 'Material Design Icons' !important;
    line-height: 1 !important;
    display: flex !important;
    align-items: center !important;
    gap: 4px !important;
  }

  /* Make star icon golden using gradient mask */
  .va-btn-primary::before::first-letter {
    background: linear-gradient(135deg, #F59E0B 0%, #D97706 100%) !important;
    -webkit-background-clip: text !important;
    -webkit-text-fill-color: transparent !important;
    background-clip: text !important;
  }

  /* Disable interaction only for non-premium users */
  .va-assistant-interface:not(.is-premium-user) .va-btn-primary {
    opacity: 0.75 !important;
    cursor: not-allowed !important;
    pointer-events: none !important;
    filter: grayscale(30%) !important;
  }

  /* Pause/Resume button - Always show premium badge, only disable for non-premium users */
  .va-btn-secondary {
    position: relative !important;
    overflow: visible !important;
  }

  .va-btn-secondary::before {
    content: '\F04CE Premium' !important;
    position: absolute !important;
    top: -10px !important;
    right: 8px !important;
    background: linear-gradient(135deg, #DC3545 0%, #C82333 100%) !important;
    color: white !important;
    font-size: 0.625rem !important;
    font-weight: 700 !important;
    padding: 4px 10px !important;
    border-radius: 12px !important;
    box-shadow: 0 2px 8px rgba(220, 53, 69, 0.5) !important;
    letter-spacing: 0.5px !important;
    z-index: 10 !important;
    white-space: nowrap !important;
    font-family: 'Material Design Icons' !important;
    line-height: 1 !important;
    display: flex !important;
    align-items: center !important;
    gap: 4px !important;
  }

  /* Disable interaction only for non-premium users */
  .va-assistant-interface:not(.is-premium-user) .va-btn-secondary {
    opacity: 0.75 !important;
    cursor: not-allowed !important;
    pointer-events: none !important;
    filter: grayscale(30%) !important;
  }

  /* Send Text Reply button - Always show premium badge, only disable for non-premium users */
  .va-text-reply {
    position: relative !important;
    overflow: visible !important;
  }

  .va-text-reply::before {
    content: '\F04CE Premium' !important;
    position: absolute !important;
    top: -8px !important;
    right: 8px !important;
    background: linear-gradient(135deg, #DC3545 0%, #C82333 100%) !important;
    color: white !important;
    font-size: 0.5625rem !important;
    font-weight: 700 !important;
    padding: 3px 8px !important;
    border-radius: 10px !important;
    box-shadow: 0 2px 8px rgba(220, 53, 69, 0.5) !important;
    letter-spacing: 0.5px !important;
    z-index: 10 !important;
    white-space: nowrap !important;
    font-family: 'Material Design Icons' !important;
    line-height: 1 !important;
    display: flex !important;
    align-items: center !important;
    gap: 3px !important;
  }

  /* Disable interaction only for non-premium users */
  .va-assistant-interface:not(.is-premium-user) .va-text-reply {
    opacity: 0.75 !important;
    cursor: not-allowed !important;
    pointer-events: none !important;
    filter: grayscale(30%) !important;
  }

  /* Send Voice Reply button - Always show premium badge, only disable for non-premium users */
  .va-voice-reply {
    position: relative !important;
    overflow: visible !important;
  }

  .va-voice-reply::before {
    content: '\F04CE Premium' !important;
    position: absolute !important;
    top: -8px !important;
    right: 8px !important;
    background: linear-gradient(135deg, #DC3545 0%, #C82333 100%) !important;
    color: white !important;
    font-size: 0.5625rem !important;
    font-weight: 700 !important;
    padding: 3px 8px !important;
    border-radius: 10px !important;
    box-shadow: 0 2px 8px rgba(220, 53, 69, 0.5) !important;
    letter-spacing: 0.5px !important;
    z-index: 10 !important;
    white-space: nowrap !important;
    font-family: 'Material Design Icons' !important;
    line-height: 1 !important;
    display: flex !important;
    align-items: center !important;
    gap: 3px !important;
  }

  /* Disable interaction only for non-premium users */
  .va-assistant-interface:not(.is-premium-user) .va-voice-reply {
    opacity: 0.75 !important;
    cursor: not-allowed !important;
    pointer-events: none !important;
    filter: grayscale(30%) !important;
  }

  /* Allow overflow for premium badges on controls section */
  .va-controls-section {
    overflow: visible !important;
  }

  /* Allow overflow for premium badges on send actions */
  .va-send-actions {
    overflow: visible !important;
  }

  /* Voice-related guide items - Always show premium indicator */
  .va-guide-item:nth-child(1) .va-guide-chip::after,  /* Speak guide */
  .va-guide-item:nth-child(2) .va-guide-chip::after,  /* Pause guide */
  .va-guide-item:nth-child(4) .va-guide-chip::after,  /* Send Text guide */
  .va-guide-item:nth-child(5) .va-guide-chip::after { /* Send Voice guide */
    content: '\F04CE' !important;
    margin-left: 4px !important;
    font-size: 0.875rem !important;
    font-family: 'Material Design Icons' !important;
  }

  /* Only gray out guide items for non-premium users */
  .va-assistant-interface:not(.is-premium-user) .va-guide-item:nth-child(1),
  .va-assistant-interface:not(.is-premium-user) .va-guide-item:nth-child(2),
  .va-assistant-interface:not(.is-premium-user) .va-guide-item:nth-child(4),
  .va-assistant-interface:not(.is-premium-user) .va-guide-item:nth-child(5) {
    opacity: 0.7 !important;
  }

  .va-assistant-interface:not(.is-premium-user) .va-guide-item:nth-child(1) .va-guide-text,
  .va-assistant-interface:not(.is-premium-user) .va-guide-item:nth-child(2) .va-guide-text,
  .va-assistant-interface:not(.is-premium-user) .va-guide-item:nth-child(4) .va-guide-text,
  .va-assistant-interface:not(.is-premium-user) .va-guide-item:nth-child(5) .va-guide-text {
    color: var(--va-text-tertiary) !important;
  }

  /* Prevent hover effects on disabled voice buttons (only for non-premium users) */
  .va-assistant-interface:not(.is-premium-user) .va-btn-primary:hover,
  .va-assistant-interface:not(.is-premium-user) .va-btn-secondary:hover,
  .va-assistant-interface:not(.is-premium-user) .va-text-reply:hover,
  .va-assistant-interface:not(.is-premium-user) .va-voice-reply:hover {
    transform: none !important;
    box-shadow: var(--va-shadow-md) !important;
  }

  /* Hide sound waves animation for premium feature */
  .va-sound-waves {
    display: none !important;
  }

  /* Premium feature info message (only show for non-premium users) */
  .va-assistant-interface:not(.is-premium-user) .va-premium-info {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 0.5rem !important;
    padding: 0.75rem 1rem !important;
    margin: 0 0 1rem 0 !important;
    background: linear-gradient(135deg, rgba(220, 53, 69, 0.1) 0%, rgba(200, 35, 51, 0.05) 100%) !important;
    border: 1px solid rgba(220, 53, 69, 0.2) !important;
    border-radius: var(--va-radius-lg) !important;
    font-size: 0.8125rem !important;
    color: var(--va-text-secondary) !important;
  }

  /* Hide premium info for premium users */
  .va-assistant-interface.is-premium-user .va-premium-info {
    display: none !important;
  }

  .va-premium-info-icon {
    font-size: 1.125rem !important;
    flex-shrink: 0 !important;
  }

  .va-premium-info-text {
    line-height: 1.4 !important;
    text-align: center !important;
  }

  .va-premium-info-text strong {
    color: #DC3545 !important;
    font-weight: 700 !important;
  }

  /* ==================== RESPONSIVE - FLOATING ORB ==================== */

  /* Tablet - 768px and up */
  @media (min-width: 768px) {
    .va-assistant-orb {
      bottom: 5rem !important;
      right: 2.5rem !important;
    }

    .va-orb-container {
      width: 4.5rem !important;
      height: 4.5rem !important;
    }
  }

  /* Desktop - 1024px and up */
  @media (min-width: 1024px) {
    .va-assistant-orb {
      bottom: 5rem !important;
      right: 3rem !important;
    }

    .va-orb-container {
      width: 5rem !important;
      height: 5rem !important;
    }
  }

  /* Mobile - below 640px */
  @media (max-width: 640px) {
    .va-assistant-orb {
      right: 1rem !important;
      left: auto !important;
    }

    .va-orb-container {
      width: 3.5rem !important;
      height: 3.5rem !important;
    }

    .va-btn-primary::before,
    .va-btn-secondary::before {
      font-size: 0.5625rem !important;
      padding: 3px 8px !important;
      top: -8px !important;
      right: 6px !important;
    }

    .va-text-reply::before,
    .va-voice-reply::before {
      font-size: 0.5rem !important;
      padding: 2px 6px !important;
      top: -7px !important;
      right: 6px !important;
    }

    .va-premium-info {
      font-size: 0.75rem !important;
      padding: 0.625rem 0.875rem !important;
    }

    .va-premium-info-icon {
      font-size: 1rem !important;
    }
  }

  /* Small Mobile - below 375px */
  @media (max-width: 375px) {
    .va-orb-container {
      width: 3rem !important;
      height: 3rem !important;
    }

    .va-btn-primary::before,
    .va-btn-secondary::before {
      font-size: 0.5rem !important;
      padding: 2px 6px !important;
      top: -7px !important;
      right: 4px !important;
    }

    .va-text-reply::before,
    .va-voice-reply::before {
      font-size: 0.4375rem !important;
      padding: 2px 5px !important;
      top: -6px !important;
      right: 4px !important;
    }

    .va-premium-info {
      font-size: 0.6875rem !important;
      padding: 0.5rem 0.75rem !important;
    }
  }
</style>